import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  closeInAppBrowser,
  getLaunchUrl,
  isFlagship,
  isNative,
  isTrustedMerchantOrigin,
  onAppUrlOpen,
} from '@core/platform';
import { useMerchantStore } from '@core/store/merchantStore';

/**
 * Routes Universal Link / App Link opens into the in-app router.
 *
 * Two jobs:
 *
 * 1. **Payment return.** The gateway redirects the in-app browser to
 *    `<merchant domain>/payment/return?...`, the OS matches it against the
 *    app's associated domains and fires `appUrlOpen`, and we turn it back into
 *    an in-app navigation. Without this the user is left staring at the web
 *    build inside a browser sheet with the app idle behind it.
 *
 * 2. **Merchant entry (flagship build).** A link to a merchant's domain both
 *    selects that merchant and navigates to the path. This is how a customer
 *    reaches a specific merchant in the multi-merchant app without a directory
 *    to browse. See ADR 0005.
 *
 * Renders nothing and does nothing on web.
 */
export default function NativeDeepLinkBootstrap() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative()) return undefined;

    const handle = (rawUrl) => {
      if (!rawUrl) return;

      let parsed;
      try {
        parsed = new URL(rawUrl);
      } catch {
        return;
      }

      // A link from an untrusted host is not ours to act on: in the flagship
      // build it would point the app at an arbitrary merchant, and in a
      // dedicated build it has no business switching merchants at all.
      if (!isTrustedMerchantOrigin(parsed.origin)) return;

      // Flagship only. A dedicated build's merchant is fixed at build time, so
      // leave its selection empty and let merchant.json stay authoritative.
      if (isFlagship()) {
        const current = useMerchantStore.getState().selectedDomain;
        if (current !== parsed.origin) {
          useMerchantStore.getState().setMerchantDomain(parsed.origin);
        }
      }

      closeInAppBrowser();
      navigate(`${parsed.pathname}${parsed.search}${parsed.hash}`, { replace: true });
    };

    // Cold launch: appUrlOpen never fires for the link that started the app.
    let cancelled = false;
    getLaunchUrl().then((url) => {
      if (!cancelled) handle(url);
    });

    const unsubscribe = onAppUrlOpen(handle);

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [navigate]);

  return null;
}
